import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  archiveContract,
  createContract,
  extractContractFromPdf,
  getContract,
  listContracts,
  updateContract,
  type ContractFilters,
} from './api';
import type { Contract, CreateContractInput, UpdateContractInput } from './types';

const ROOT = ['contracts'] as const;

export function useContracts(filters: ContractFilters = {}) {
  return useQuery({
    queryKey: [...ROOT, 'list', filters] as const,
    queryFn: () => listContracts(filters),
    staleTime: 30_000,
  });
}

export function useContract(contractUrn: string | undefined) {
  return useQuery({
    queryKey: [...ROOT, contractUrn ?? ''] as const,
    queryFn: () => getContract(contractUrn as string),
    enabled: Boolean(contractUrn),
  });
}

export function useCreateContract() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateContractInput) => createContract(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ROOT });
    },
  });
}

export function useUpdateContract(contractUrn: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateContractInput) => updateContract(contractUrn, input),
    onSuccess: (updated: Contract) => {
      qc.setQueryData([...ROOT, contractUrn], updated);
      void qc.invalidateQueries({ queryKey: [...ROOT, 'list'] });
    },
  });
}

export function useArchiveContract(contractUrn: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => archiveContract(contractUrn),
    onSuccess: (updated: Contract) => {
      qc.setQueryData([...ROOT, contractUrn], updated);
      void qc.invalidateQueries({ queryKey: [...ROOT, 'list'] });
    },
  });
}

export function useExtractContractFromPdf() {
  return useMutation({
    mutationFn: (file: File) => extractContractFromPdf(file),
  });
}
